import { Link } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { newsList } from "@/mocks/news";
import { sortMedia, typeLabelMap } from "@/utils/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import RecentHistory from "@/pages/home/components/RecentHistory";
import FavoritesRail from "@/pages/home/components/FavoritesRail";
import HotSection from "@/pages/home/components/HotSection";
import MediaCard from "@/components/feature/MediaCard";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/feature/SectionHeading";

export default function Home() {
  useDocumentMeta(
    "日韩影视大全 - 热门日韩电影电视剧推荐",
    "日韩影视大全网站提供最新最全的日韩电影、电视剧资源，涵盖多种类型，满足用户影视内容浏览需求。这里可以查找热门日韩影视作品信息和相关资讯。",
    "日韩,日韩影视,日韩电影,日韩电视剧,日韩影视推荐,日韩影视资讯,日韩影视大全",
  );

  const featured = mediaList.filter((item) => item.featured);
  const latest = sortMedia(mediaList, "latest").slice(0, 8);
  const mainNews = newsList[0];
  const sideNews = newsList.slice(1, 4);
  const totalGenres = new Set(mediaList.flatMap((item) => item.genres)).size;

  return (
    <div className="w-full pb-14">
      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 pt-5 md:pt-7">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.22em] text-foreground-500">
              <span className="h-px w-6 bg-accent-500" />
              Japan · Korea Cinema
            </p>
            <h1 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-[30px]">
              日韩影视大全
            </h1>
          </div>
          <p className="text-xs text-foreground-500">
            本周更新 · 收录 <span className="font-medium text-foreground-900">{mediaList.length}</span> 部作品 /{" "}
            <span className="font-medium text-foreground-900">{newsList.length}</span> 篇资讯
          </p>
        </div>

        <div className="pt-4 md:pt-5">
          <HeroCarousel items={featured} />
        </div>

        <div className="mt-4 md:mt-5">
          <CategoryQuickLinks />
        </div>

        <RecentHistory />

        <FavoritesRail />

        <section className="py-8 md:py-12">
          <SectionHeading
            eyebrow="New Arrivals"
            title="最新影视作品"
            description="按上线年份与站内热度整理的新收录内容，封面、类型与年份一目了然。"
            actionLabel="查看全部作品"
            actionTo="/category/all"
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
            {latest.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        <HotSection />

        <section className="py-8 md:py-12">
          <SectionHeading
            eyebrow="Editorial"
            title="影视资讯快报"
            description="新片定档、影展现场、追剧指南与行业观察，掌握日韩影视的最新动向。"
            actionLabel="全部资讯"
            actionTo="/news"
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Link
                to={`/news/${mainNews.id}`}
                className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50"
              >
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200">
                  <img
                    src={mainNews.cover}
                    alt={`${mainNews.title} 配图`}
                    title={`${mainNews.title} - 日韩影视资讯`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 ease-smooth group-hover:scale-[1.03]"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-primary-950">
                    头条
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-foreground-500">
                    <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-800">
                      {mainNews.category}
                    </span>
                    <time dateTime={mainNews.date}>{mainNews.date.replace(/-/g, ".")}</time>
                    <span className="text-background-400">·</span>
                    <span>{mainNews.readTime}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
                    {mainNews.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                    {mainNews.excerpt}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700">
                    阅读专题
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </div>
              </Link>
            </div>

            <div className="flex flex-col gap-3 lg:col-span-5">
              {sideNews.map((item) => (
                <article
                  key={item.id}
                  className="rounded-lg border border-background-200/80 bg-background-50 p-4"
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-500">
                    <span className="text-primary-700">{item.category}</span>
                    <span className="text-background-400">·</span>
                    <time dateTime={item.date}>{item.date.replace(/-/g, ".")}</time>
                  </div>
                  <NewsCard item={item} variant="compact" />
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {item.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-sm bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-500"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </article>
              ))}
              <Link
                to="/news"
                className="flex cursor-pointer items-center justify-between rounded-lg border border-dashed border-background-300 px-4 py-3 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                浏览全部 {newsList.length} 篇影视资讯
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="relative overflow-hidden rounded-xl bg-primary-950 px-6 py-9 md:px-12 md:py-12">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent-500/10 blur-3xl" />
            <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
              <div>
                <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.22em] text-accent-300">
                  <span className="h-px w-6 bg-accent-500" />
                  About Rihan Cinema
                </p>
                <h2 className="mt-3 font-heading text-2xl font-semibold leading-snug text-background-50 md:text-[28px]">
                  一个认真整理日韩影视的资料库
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-300">
                  「日韩」专注收录日本与韩国的电影、电视剧和综艺内容，为追剧用户、资料查询者和影视资讯关注者提供结构清晰的浏览体验。每部作品都附有完整的基础资料、剧情简介和同类推荐。
                </p>
                <ul className="mt-5 space-y-2.5">
                  {["多维筛选：类型、地区、年份自由组合", "详情资料：导演、主演、片长与剧情简介齐全", "收藏与浏览记录保存在本地，刷新后依然保留"].map(
                    (text) => (
                      <li key={text} className="flex items-start gap-2 text-sm text-background-200">
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent-500 text-primary-950">
                          <i className="ri-check-line text-[10px]" />
                        </span>
                        {text}
                      </li>
                    ),
                  )}
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/category/all"
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    开始浏览作品
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                  <Link
                    to="/news"
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 px-5 py-2.5 text-sm font-medium text-background-100 transition-colors hover:border-background-50/50 hover:bg-background-50/10"
                  >
                    阅读影视资讯
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 self-center">
                {[
                  { value: mediaList.length, unit: "部", label: "收录影视作品" },
                  { value: newsList.length, unit: "篇", label: "影视资讯专题" },
                  { value: totalGenres, unit: "类", label: "覆盖题材类型" },
                  { value: 2, unit: "国", label: "日本 · 韩国" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg border border-background-50/10 bg-background-50/5 px-4 py-4"
                  >
                    <p className="font-heading text-2xl font-semibold text-background-50 md:text-[28px]">
                      {stat.value}
                      <span className="ml-1 text-xs font-normal text-accent-300">{stat.unit}</span>
                    </p>
                    <p className="mt-1 text-[11px] text-background-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="flex flex-wrap items-center justify-between gap-4 border-t border-background-200/80 pt-8">
          <div>
            <p className="text-xs text-foreground-500">热门分类入口</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(["movie", "tv", "variety"] as const).map((type) => (
                <Link
                  key={type}
                  to={`/category/${type}`}
                  className="cursor-pointer rounded-full border border-background-300/80 bg-background-50 px-3 py-1.5 text-[11px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  日韩{typeLabelMap[type]}
                </Link>
              ))}
              <Link
                to="/search"
                className="cursor-pointer rounded-full border border-background-300/80 bg-background-50 px-3 py-1.5 text-[11px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                搜索作品
              </Link>
            </div>
          </div>
          <p className="max-w-md text-[11px] leading-relaxed text-foreground-400">
            本站影视作品、评分与资讯均为虚构演示数据，用于展示浏览、筛选、搜索与收藏等功能流程。
          </p>
        </section>
      </div>
    </div>
  );
}